import React from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Paper,
  Alert
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { downloadBlob } from '../../../../../modules/refunds/utils/documentUtils';

interface CarveOutHelpDialogProps {
  open: boolean;
  onClose: () => void;
}

const CarveOutHelpDialog: React.FC<CarveOutHelpDialogProps> = ({ open, onClose }) => {
  const handleDownloadTemplate = () => {
    const headers = ['Code', 'Description', 'Rate', 'ApplyMultiProcedureDisc', 'IsCaseRate'];
    const csvContent = headers.join(',');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, 'carveout_template.csv');
  };

  const csvColumns = [
    { field: 'Code', description: 'Unique identifier for the CarveOut (required)', example: 'CV001' },
    { field: 'Description', description: 'Description of the CarveOut service (required)', example: 'Cardiac Surgery Package' },
    { field: 'Rate', description: 'Rate amount in dollars (required)', example: '15000.00' },
    { field: 'ApplyMultiProcedureDisc', description: 'Whether to apply ApplyMultiProcedureDisc (true/false)', example: 'true' },
    { field: 'IsCaseRate', description: 'Whether this is a IsCaseRate (true/false)', example: 'false' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>CarveOut Management Help</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              About CarveOuts
            </Typography>
            <Typography variant="body2" paragraph>
              CarveOuts are special pricing arrangements that override standard fee schedules for specific procedures or services.
              They are directly associated with contracts and provide custom rates for specific scenarios.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              CSV Upload Format
            </Typography>
            <Typography variant="body2" paragraph>
              Upload a CSV file with the following columns. The first row should contain column headers:
            </Typography>
            
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Column Name</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>Example</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {csvColumns.map((column) => (
                    <TableRow key={column.field}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {column.field}
                      </TableCell>
                      <TableCell>{column.description}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {column.example}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Alert severity="info">
            <Typography variant="body2">
              <strong>Import Behavior:</strong> Uploading a CSV file will replace all existing CarveOuts for this contract.
              Make sure to include all CarveOuts you want to keep in your CSV file.
            </Typography>
          </Alert>

          <Box>
            <Typography variant="h6" gutterBottom>
              Field Details
            </Typography>
            <Box sx={{ ml: 2 }}>
              <Typography variant="body2" paragraph>
                <strong>Code:</strong> Must be unique within the contract. Use consistent naming conventions.
              <Typography variant="body2" paragraph>
                <strong>Description:</strong> Description of the CarveOut service or procedure.
              </Typography>
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Rate:</strong> Enter as decimal number (e.g., 1500.00 for $1,500).
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>ApplyMultiProcedureDisc:</strong> Boolean value indicating if this CarveOut should be subject to ApplyMultiProcedureDisc rules. Accepts: Y, N, Yes, No, True, False, 1, or 0.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>IsCaseRate:</strong> Boolean value indicating if this rate applies to the entire case rather than per procedure. Accepts: Y, N, Yes, No, True, False, 1, or 0.
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              Example CSV Content
            </Typography>
            <Box sx={{ 
              bgcolor: 'grey.100', 
              p: 2, 
              borderRadius: 1, 
              fontFamily: 'monospace', 
              fontSize: '0.875rem',
              overflow: 'auto'
            }}>
              <pre>{`Code,Description,Rate,ApplyMultiProcedureDisc,IsCaseRate
CV001,Cardiac Surgery Package,15000.00,false,true
CV002,Orthopedic Joint Replacement,12500.50,true,false
CV003,Neurosurgery Package,18000.00,false,true`}</pre>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadTemplate}
          size="small"
        >
          Download Template
        </Button>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CarveOutHelpDialog;
